import { useState, useRef, useCallback, useEffect } from 'react';
import ExcerptCreateModal from './ExcerptCreateModal';
import TimeField from '../media/TimeField';
import { formatTimecode, formatDurationWords } from '../../domain/time';

/**
 * Excerpt Studio — mark ONE in/out range on a recording and cut it into its
 * own document, transcribed in full. Docks under the player beside the
 * Redaction Studio; invisible unless `features.media_excerpts` is on.
 *
 * Three ways to set the range, all feeding the same draft: drag on the
 * strip, ✂ on transcript lines (wired in the panels via onExcerptRange),
 * and the IN/OUT fields with playhead snap. "Play excerpt" loops playback
 * inside the range. See plans/MEDIA_EXCERPT_PLAN.md.
 *
 * On a document that IS an excerpt, the studio instead shows where it came
 * from and opens the original at the IN point.
 */

const fmt = (s) => formatTimecode(s);

function ExcerptStrip({ excerpt, currentTime, onSeek }) {
  const stripRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const { duration, range } = excerpt;

  const timeAt = useCallback((clientX) => {
    const rect = stripRef.current.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return frac * duration;
  }, [duration]);

  const onMouseDown = (e) => {
    if (!duration || e.button !== 0) return;
    e.preventDefault();
    const start = timeAt(e.clientX);
    setDraft({ start, end: start });
    const onMove = (me) => setDraft({ start, end: timeAt(me.clientX) });
    const onUp = (ue) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      const end = timeAt(ue.clientX);
      setDraft(null);
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      if (hi - lo >= 0.3) excerpt.setRange(lo, hi, 'strip');
      else onSeek?.(lo); // a click (no drag) just seeks
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!duration) {
    return <div className="rdx-strip rdx-strip--empty xpt-strip">Player loading…</div>;
  }
  const pct = (t) => `${(Math.min(t, duration) / duration) * 100}%`;
  return (
    <div
      className="rdx-strip xpt-strip"
      ref={stripRef}
      onMouseDown={onMouseDown}
      title="Drag to mark the excerpt; click to seek"
    >
      {range && (
        <div
          className="rdx-strip-block xpt-strip-block"
          style={{ left: pct(range.start), width: pct(range.end - range.start) }}
          title={`Excerpt ${fmt(range.start)}–${fmt(range.end)}`}
        />
      )}
      {draft && (
        <div
          className="rdx-strip-block xpt-strip-block--draft"
          style={{
            left: pct(Math.min(draft.start, draft.end)),
            width: pct(Math.abs(draft.end - draft.start)),
          }}
        />
      )}
      <div className="rdx-strip-playhead" style={{ left: pct(currentTime || 0) }} />
    </div>
  );
}

function ProvenanceBar({ excerptRecord, onOpenDocument }) {
  const start = Number(excerptRecord?.time_start);
  const end = Number(excerptRecord?.time_end);
  const sourceId = excerptRecord?.source_document_id || null;
  const sourceName = excerptRecord?.source_filename || 'the original';
  return (
    <div className="rdx-studio xpt-studio">
      <div className="rdx-bar">
        <span className="xpt-provenance" title="This document is an excerpt cut from a longer recording">
          ✂ Excerpt of <strong>{sourceName}</strong>
          {Number.isFinite(start) && Number.isFinite(end) && (
            <> · {fmt(start)}–{fmt(end)}</>
          )}
        </span>
        <button
          className="dv-transcript-toggle"
          type="button"
          disabled={!onOpenDocument || !sourceId}
          onClick={() => onOpenDocument?.(sourceId, { startAt: Number.isFinite(start) ? start : null })}
          title={sourceId ? 'Open the original recording at the point this excerpt starts' : 'The original is no longer in the case'}
        >
          <span>Open the original here</span>
        </button>
      </div>
    </div>
  );
}

export default function ExcerptStudio({
  excerpt, caseId, documentId, currentTime, onSeek, isVideo = false,
  excerptRecord = null, onOpenDocument = null,
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [markedNote, setMarkedNote] = useState(null);
  useEffect(() => {
    if (!excerpt.lastMark) return;
    setMarkedNote(excerpt.lastMark);
    const tid = setTimeout(() => setMarkedNote(null), 5000);
    return () => clearTimeout(tid);
  }, [excerpt.lastMark]);

  // An excerpt document: show where it came from (even if the flag is off —
  // the document exists regardless).
  if (excerptRecord) {
    return <ProvenanceBar excerptRecord={excerptRecord} onOpenDocument={onOpenDocument} />;
  }
  if (!excerpt.enabled) return null;

  const { range, duration } = excerpt;
  const playExcerpt = () => {
    if (!range) return;
    excerpt.setLoop(true);
    onSeek?.(range.start);
  };

  return (
    <div className="rdx-studio xpt-studio">
      <div className="rdx-bar">
        <button
          className={`dv-transcript-toggle ${excerpt.mode ? 'dv-transcript-toggle--active' : ''}`}
          type="button"
          onClick={() => excerpt.setMode(!excerpt.mode)}
          title="Cut a portion of this recording into its own document, transcribed in full"
        >
          <span>✂ Excerpt</span>
          {range && <span className="dv-transcript-toggle-count">1</span>}
        </button>
        {excerpt.mode && (
          <>
            <span className="rdx-hint">
              Drag on the strip, use ✂ on a transcript line, or set IN/OUT at the playhead
            </span>
            {range && (
              <button
                className="btn rdx-render-btn"
                type="button"
                disabled={!excerpt.valid}
                onClick={() => setCreateOpen(true)}
                title={excerpt.valid ? 'Cut this range into its own document' : 'Adjust the range first'}
              >
                Create excerpt
              </button>
            )}
          </>
        )}
      </div>
      {excerpt.mode && (
        <>
          <ExcerptStrip excerpt={excerpt} currentTime={currentTime} onSeek={onSeek} />
          <div className="xpt-range-row">
            <span className="xpt-range-label">IN</span>
            <button
              type="button" className="rdx-snap"
              onClick={() => excerpt.setStart(currentTime || 0)}
              title="Set IN to the current playhead position"
            >⇤</button>
            <TimeField
              value={range ? range.start : (currentTime || 0)}
              onCommit={excerpt.setStart}
              ariaLabel="Excerpt start time"
            />
            <span className="rdx-range-dash">–</span>
            <TimeField
              value={range ? range.end : Math.min(duration || 0, (currentTime || 0) + 10)}
              onCommit={excerpt.setEnd}
              ariaLabel="Excerpt end time"
            />
            <button
              type="button" className="rdx-snap"
              onClick={() => excerpt.setEnd(currentTime || 0)}
              title="Set OUT to the current playhead position"
            >⇥</button>
            <span className="xpt-range-label">OUT</span>
            <button
              type="button" className={`rdx-row-play${excerpt.loop ? ' xpt-loop--on' : ''}`}
              disabled={!range}
              onClick={excerpt.loop ? () => excerpt.setLoop(false) : playExcerpt}
              title={excerpt.loop ? 'Stop looping the excerpt' : 'Play just the excerpt, on a loop'}
            >
              {excerpt.loop ? '⟲ Looping' : '▶ Play excerpt'}
            </button>
            {range && (
              <button
                type="button" className="rdx-snap"
                onClick={excerpt.clear}
                title="Clear the range"
              >✕</button>
            )}
          </div>
          <div className="xpt-cost-row">
            {range ? (
              <>
                <span>
                  <strong>{formatDurationWords(excerpt.lengthSeconds)}</strong>
                  {' · '}about <strong>${excerpt.estimatedCost.toFixed(2)}</strong> to transcribe in full
                  {' · '}audio stays inside the Google boundary
                </span>
                {excerpt.tooShort && (
                  <span className="rdx-error"> — at least {excerpt.minSeconds}s</span>
                )}
                {excerpt.tooLong && (
                  <span className="rdx-error">
                    {' '}— at most {Math.round(excerpt.maxSeconds / 60)} minutes; for more, Deep search the whole file
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted">No range yet.</span>
            )}
            {isVideo && (
              <label className="rdx-preview-toggle" title="Re-encode so the excerpt starts on the exact frame (slower). Off = stream copy: seconds, may start a keyframe early.">
                <input
                  type="checkbox"
                  checked={excerpt.cutMode === 'exact'}
                  onChange={e => excerpt.setCutMode(e.target.checked ? 'exact' : 'fast')}
                />
                Exact to the frame
              </label>
            )}
          </div>
          {markedNote && (
            <div className="rdx-added-note">
              ✓ Range {fmt(markedNote.start)}–{fmt(markedNote.end)}
              {markedNote.via === 'transcript' ? ' — more ✂ lines extend it' : ''}
            </div>
          )}
        </>
      )}
      {createOpen && (
        <ExcerptCreateModal
          caseId={caseId}
          documentId={documentId}
          excerpt={excerpt}
          isVideo={isVideo}
          onClose={() => setCreateOpen(false)}
          onSubmitted={() => { excerpt.clear(); excerpt.setMode(false); }}
        />
      )}
    </div>
  );
}
